import {
  IReactiveModel,
  MicrofrontendManager,
  RecognitionManager,
  ViewModel,
} from '@descomplica/microfrontend-core';
import { action, computed, makeObservable, observable } from 'mobx';

import { GetIdentifiedUserCollaboration } from '@/application/collaborations/get-identified-user.collaboration';
import type { UserDto } from '@/application/interfaces/pegasus.gateway';
import envs from '@/infrastructure/config/envs';

import { ApplicationModel } from './models/application.model';
import { ClassifiersModel } from './models/classifiers.model';
import { UserModel } from './models/user.model';

@ViewModel({ singleton: true })
export class MicrofrontendViewModel implements IReactiveModel {
  @observable isLoading = true;
  @observable hasError: boolean;
  @observable user: UserDto;

  constructor(
    private readonly userModel: UserModel,
    private readonly applicationModel: ApplicationModel,
    private readonly classifiersModel: ClassifiersModel,
    private readonly recognitionManager: RecognitionManager,
    private getIdentifiedUserCollaboration: GetIdentifiedUserCollaboration,
  ) {
    makeObservable(this);
  }

  @action
  async onInit() {
    try {
      await this.initializeMicrofrontend();

      await Promise.all([
        this.userModel.getIdentifiedAccount(),
        this.applicationModel.getApplication(),
        this.classifiersModel.getClassifiers(),
      ]);
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async initializeMicrofrontend() {
    await MicrofrontendManager.init({
      pegasusUri: envs.pegasusUrl,
      caching: {
        namespace: envs.cachingNamespace,
      },
      settings: {
        launchDarklyClientId: envs.launchDarklyClientId,
      },
      recognition: {
        isEnabled: true,
        forceRedirectToSignIn: true,
        forceReautenticate: true,
        signInUrl: envs.recognitionSigninUrl,
        reauthenticateUrl: envs.recognitionReauthenticateUrl,
      },
      analytics: {
        isEnabled: true,
        mixpanelToken: process.env?.REACT_APP_MIXPANEL_TOKEN,
        hubspotToken: process.env?.REACT_APP_HUBSPOT_API_KEY,
      },
    });
  }

  @computed
  get hasPermissionError() {
    return this.userModel.hasPermissionError;
  }

  @action
  async clearAccount() {
    return this.recognitionManager.clearCredentials();
  }

  @action
  async getIdentifiedAccount() {
    try {
      const { data: user } = await this.getIdentifiedUserCollaboration.run();
      this.user = user;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        return this.userModel.hasPermissionError;
      }
    }
  }
}
